import React, { useState, useEffect } from 'react';
import { X, Zap, Download, Share2, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Progress } from '@/components/ui/Progress';
import { useBottomBar } from '../providers/BottomBarProvider';
import { useVideos } from '../providers/VideosProvider';
import { sendRequest } from '../api';
import { io } from "socket.io-client";
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';

const BottomBar = ({ 
  showBatchActions,
  selectedVideos,
  clearSelections,
  onCrossPost,
  onAutomation,
}) => {
  const { bottomBarError, setBottomBarError } = useBottomBar();
  const { isDownloading, downloadId, setDownloadId, setIsDownloading } = useVideos();
  const [completedCount, setCompletedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [progress, setProgress] = useState(0);
  const [socket, setSocket] = useState(null);
  const [failedCount, setFailedCount] = useState(0);
  const [failures, setFailures] = useState([]);
  const [showFailuresModal, setShowFailuresModal] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const resetStates = () => {
    setProgress(0);
    setCompletedCount(0);
    setTotalCount(0);
    setFailedCount(0);
    setFailures([]);
    setDownloadId(null);
    setIsDownloading(false);
    setDownloadUrl(null);
    setBottomBarError(null);
  };

  // Reset all states when bottom bar is hidden
  useEffect(() => {
    if (!showBatchActions) {
      resetStates();
      // Don't reset download state when cross-posting
      if (!downloadUrl) {
        setIsDownloading(false);
        setDownloadId(null);
      }
      
      // Cleanup socket connection
      if (socket) {
        socket.off("message", handleEvent);
        socket.disconnect();
        setSocket(null);
      }
    }
  }, [showBatchActions]);

  // Reset states when download completes or is cancelled
  useEffect(() => {
    if (!downloadId) {
      setCompletedCount(0);
      setTotalCount(0);
      setFailedCount(0);
      setFailures([]);
      
      // Cleanup socket connection
      if (socket) {
        socket.off("message", handleEvent);
        socket.disconnect();
        setSocket(null);
      }
    }
  }, [downloadId]);

  // Expose resetStates through BottomBarProvider
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.bottomBarResetStates = resetStates;
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.bottomBarResetStates;
      }
    };
  }, []);

  // Socket connection
  useEffect(() => {
    if (!socket && downloadId) {
      const newSocket = io(process.env.WEBSOCKET_URL);
      setSocket(newSocket);
      newSocket.on("message", handleEvent);
    }

    return () => {
      if (socket) {
        socket.off("message", handleEvent);
        socket.disconnect();
        setSocket(null);
      }
    };
  }, [downloadId]);

  // Handle WebSocket events
  const handleEvent = (data) => {
    if (downloadId && data && data.startsWith(downloadId)) {
      if (data.includes('-failures-')) {
        const [_, __, count] = data.split('-');
        const failedCount = parseInt(count);
        setFailedCount(failedCount);
        if (failedCount > 0) {
          async function run() {
            await fetchFailures();
            if (failedCount === selectedVideos.size) {
              setIsDownloading(false);
              stopDownload();
              setProgress(0);
            }
          }
          run();
        }
      } else {
        const [_, msgProgress, completed, total] = data.split('-');
        setProgress(parseInt(msgProgress));
        if (completed && total) {
          setCompletedCount(parseInt(completed));
          setTotalCount();
        }
        if (msgProgress === "100") {
          fetchDownloadUrl();
        }
      }
    }
  };

  const fetchFailures = async () => {
    try {
      const { failed_video_urls } = await sendRequest(`/downloads/${downloadId}/failures`);
      if (failed_video_urls) {
        setFailures(failed_video_urls.map(f => {
          try {
            return JSON.parse(f);
          } catch (e) {
            console.error('Error parsing failure:', e);
            return [f, 'Unknown error'];
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching failures:', error);
      setBottomBarError(error.message);
    }
  };

  const fetchDownloadUrl = async () => {
    try {
      const { zip_url } = await sendRequest(`/downloads/${downloadId}`);
      if (zip_url) {
        setDownloadUrl(zip_url);
        setIsDownloading(false);
      }
    } catch (error) {
      console.error('Error fetching download URL:', error);
      setBottomBarError(error.message);
    }
  };

  const renderFailuresTable = () => (
    <div className="mt-4 border rounded-lg overflow-hidden">
      <div className="max-h-[300px] overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">URL</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {failures.map(([url, reason], i) => (
              <tr key={i} className="bg-white">
                <td className="px-4 py-2 text-sm">
                  <a 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    {url.substring(0, 50)}...
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </td>
                <td className="px-4 py-2 text-sm text-gray-600">{reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const startDownload = async (selectedVideos) => {
    setIsDownloading(true);
    setProgress(0);
    setFailedCount(0);
    setFailures([]);

    try {
      const selectedVideoUrls = Array.from(selectedVideos).map(video => video.share_url);

      if (selectedVideoUrls.length === 0) {
        throw new Error("No videos selected");
      }

      const response = await sendRequest("/downloads", "POST", {
        video_urls: selectedVideoUrls,
      });

      if (response.error) {
        stopDownload();
        setBottomBarError(response.error);

        if (response.code === 403) {
          window.location.href = "/upgrade";
        }
        return;
      }

      if (response.url) {
        window.location.href = response.url;
        return;
      }

      setDownloadId(response.download_id);
      return response;
    } catch (error) {
      setBottomBarError(error.message);
      stopDownload();
    }
  };

  const stopDownload = () => {
    setIsDownloading(false);
  };

  if (!showBatchActions) return null;

  const handleClearSelections = () => {
    clearSelections();
    resetStates();
  };

  // Format the progress message
  const getProgressMessage = () => {
    if (downloadUrl) return 'Download ZIP';
    if (!isDownloading) return 'Download';
    if (totalCount === 0) return 'Processing...';
    return `Processing ${completedCount}/${selectedVideos.size}`;
  };

  return (
    <>
      <div className="fixed bottom-0 inset-x-0 md:left-20 bg-gray-800/80 backdrop-blur-lg border-t border-gray-700 shadow-lg mb-[64px] md:mb-0">
        <div className="absolute top-0 inset-x-0 -translate-y-[4px]">
          <Progress value={isDownloading ? progress : 0} className="h-[4px] rounded-none" />
        </div>
        <div className="max-w-7xl mx-auto px-6 py-4">
          {bottomBarError && (
            <div className="text-red-400 text-sm font-medium text-center mb-3">
              {bottomBarError}
            </div>
          )}
          {failedCount > 0 && (
            <div className="text-red-400 text-sm font-medium text-center mb-3">
              {failedCount === selectedVideos.size ? 'All videos failed to export.' : `${failedCount} video${failedCount !== 1 ? 's' : ''} failed to export.`}{' '}
              <button 
                onClick={() => setShowFailuresModal(true)}
                className="text-red-400 underline hover:text-red-300"
              >
                See why
              </button>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleClearSelections}
                className="text-gray-400 hover:text-gray-300 rounded-full p-1 hover:bg-gray-700 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
              <span className="hidden sm:block text-sm font-medium text-gray-300">
                {selectedVideos.size} video{selectedVideos.size !== 1 ? 's' : ''} selected
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant={downloadUrl ? "default" : "outline"}
                onClick={downloadUrl ? () => window.location.href = downloadUrl : () => startDownload(selectedVideos)}
                disabled={isDownloading && !downloadUrl}
                className={`rounded-xl min-w-[140px] p-2.5 px-4 py-2 ${
                  downloadUrl 
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg shadow-purple-500/25' 
                    : 'bg-gray-900/50 hover:bg-gray-800 text-gray-300 border-gray-700'
                }`}
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {getProgressMessage()}
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    {getProgressMessage()}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showFailuresModal} onOpenChange={setShowFailuresModal}>
        <DialogContent className="sm:max-w-2xl bg-gray-800 text-gray-300 border-gray-700">
          <DialogTitle className="text-gray-200">Download Failures</DialogTitle>
          {renderFailuresTable()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BottomBar; 